import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import SlideOutMenuSectioned from "@components/SlideOutMenu/SlideOutMenuSectioned";
import {
  canSelectColumn,
  reorderSelectedColumns,
  selectedColumnWidth,
  MAX_COLUMN_WIDTH,
} from "../../ProjectsList/columnConfig";
import type { UserViewColumn } from "../types";
import "./CustomizeTableSlideout.css";

export interface CustomizeTableSlideoutProps {
  show: boolean;
  onClose: () => void;
  columns: UserViewColumn[];
  onApply: (next: UserViewColumn[]) => void;
  onReset: () => void;
}

const isLocked = (col: UserViewColumn) =>
  col.optional === false || col.label === "Project Name";

const CustomizeTableSlideout = ({
  show,
  onClose,
  columns,
  onApply,
  onReset,
}: CustomizeTableSlideoutProps) => {
  const [draft, setDraft] = useState<UserViewColumn[]>(columns);
  const [query, setQuery] = useState("");
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const prevShow = useRef(false);

  // Reset the draft only when the slideout opens, never when the parent
  // re-renders with a new-but-equal columns array while it is open.
  useEffect(() => {
    if (show && !prevShow.current) {
      setDraft(columns);
      setQuery("");
      setDragFrom(null);
    }
    prevShow.current = show;
  }, [show, columns]);

  const searching = query.trim().length > 0;
  const matches = (col: UserViewColumn) =>
    col.label.toLowerCase().includes(query.trim().toLowerCase());

  const selected = useMemo(
    () =>
      draft.filter((x) => x.selected).sort((a, b) => a.position - b.position),
    [draft]
  );
  const available = useMemo(
    () =>
      draft.filter((x) => !x.selected).sort((a, b) => a.position - b.position),
    [draft]
  );
  const visibleSelected = selected.filter(matches);
  const visibleAvailable = available.filter(matches);
  const noMatch =
    searching && !visibleSelected.length && !visibleAvailable.length;
  const width = selectedColumnWidth(draft);
  const maxed = width >= MAX_COLUMN_WIDTH;

  const toggle = (col: UserViewColumn) =>
    setDraft((d) =>
      d.map((x) =>
        x.data_path !== col.data_path
          ? x
          : {
              ...x,
              selected: !col.selected,
              position: col.selected
                ? 0
                : d.filter((y) => y.selected).length + 1,
              sorted: "false",
            }
      )
    );

  const row = (col: UserViewColumn, index?: number) => {
    const disabled = col.selected
      ? isLocked(col)
      : !canSelectColumn(draft, col);
    return (
      <li
        key={col.data_path}
        draggable={col.selected && !searching}
        onDragStart={() => index != null && setDragFrom(index)}
        onDragEnter={() => {
          if (dragFrom == null || index == null || searching) return;
          setDraft((d) => reorderSelectedColumns(d, dragFrom, index));
          setDragFrom(index);
        }}
        onDragOver={(e) => col.selected && e.preventDefault()}
        onDrop={() => setDragFrom(null)}
        className={`customizeColumnRow${
          col.selected && !searching ? " customizeColumnRow--draggable" : ""
        }`}
      >
        <Form.Check
          type="checkbox"
          id={`col-${col.data_path}`}
          label={`${col.label} (${col.width})`}
          checked={col.selected}
          disabled={disabled}
          onChange={() => toggle(col)}
        />
      </li>
    );
  };


  return (
    <SlideOutMenuSectioned
      show={show}
      setShow={(v: boolean) => {
        if (!v) onClose();
      }}
      menuHeader="Customize Table"
      actionButtonText="Apply"
      onActionButtonClick={() => {
        onApply(draft);
        onClose();
      }}
      clearFields="Reset Table"
      onClearFieldsClick={() => {
        onReset();
        onClose();
      }}
      onCancel={() => {}}
      sectionsArray={[
        {
          sectionTitle: "",
          sectionClass: "customizeTableSection",
          sectionContent: (
            <div className="customizeTableHelp">
              <p>Check a column to add it to the table, or uncheck it to remove it.</p>
              <p>Drag selected columns to change their order.</p>
              <p>
                The number after each column is its width. Selected columns can
                use up to {MAX_COLUMN_WIDTH} in total.
              </p>
            </div>
          ),
        },
        {
          sectionTitle: [
            <span key="searchHeader" className="customizeTableHeader">
              Search Columns
            </span>,
          ],
          sectionClass: "customizeTableSection",
          sectionContent: (
            <Form
              className="customizeTableSearch"
              onSubmit={(e) => e.preventDefault()}
            >
              <Form.Control
                type="search"
                aria-label="Search columns"
                placeholder="Search by Column Name"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </Form>
          ),
        },
        {
          // SlideOutMenuSectioned only renders titles that have a length, so
          // a JSX title must be wrapped in an array.
          sectionTitle: [
            <div key="selectedHeader" className="customizeTableHeaderRow">
              <span className="customizeTableHeader">Selected Columns</span>
              <span
                className={`customizeTableWidth${
                  maxed ? " customizeTableWidth--max" : ""
                }`}
              >
                Width {width}/{MAX_COLUMN_WIDTH}
              </span>
            </div>,
          ],
          sectionClass: "customizeTableSection",
          sectionContent: (
            <>
              {maxed && (
                <p className="customizeTableNotice customizeTableNotice--max">
                  Max columns added. Remove a column to add another.
                </p>
              )}
              {noMatch && (
                <p className="customizeTableNotice">No columns match your search.</p>
              )}
              <ul data-testid="selected-columns" className="customizeColumnList">
                {visibleSelected.map((col) => row(col, selected.indexOf(col)))}
              </ul>
            </>
          ),
        },
        {
          sectionTitle: [
            <span key="availableHeader" className="customizeTableHeader">
              Available Columns
            </span>,
          ],
          sectionClass: "customizeTableSection",
          sectionContent: (
            <ul data-testid="available-columns" className="customizeColumnList">
              {visibleAvailable.map((col) => row(col))}
            </ul>
          ),
        },
      ]}
    />
  );
};

export default CustomizeTableSlideout;
